import { useEffect, useState } from 'react'
import { Box3, Vector3, Mesh, Group, EdgesGeometry, LineSegments, LineBasicMaterial, MeshStandardMaterial, DoubleSide, PointsMaterial, Points } from 'three'
import { apiGetFile } from '../../../../generic/Api_Functions'
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

// Defined in the import-free leaf module so light consumers can check compatibility
// without pulling three.js; re-exported here for backwards compatibility.
import { CompatibleModelsExtensions } from '../../documentExtensions'

const ModelLoader = ({ modelType, setModelSize, solid = true, edges = false, edgesMinAngle = 1, edgesColor = 'black', setIsPreviewLoading, onProgress, url, setModelError }) => {
  const [modelGroup, setModelGroup] = useState(null)

  // 1. Load and center the model
  useEffect(() => {
    const loadModel = async () => {
      try {
        const response = await apiGetFile(url)
        if (!response.ok) throw new Error('Network error')

        // --- CUSTOM STREAM READER FOR PROGRESS ---
        const contentLength = response.headers.get('content-length')
        const total = contentLength ? parseInt(contentLength, 10) : 0
        let loaded = 0

        const reader = response.body.getReader()
        const chunks = []

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          chunks.push(value)
          loaded += value.length
          if (total && onProgress) {
            onProgress(Math.round((loaded / total) * 100))
          }
        }

        const arrayBuffer = new Uint8Array(loaded)
        let position = 0
        for (const chunk of chunks) {
          arrayBuffer.set(chunk, position)
          position += chunk.length
        }
        // ---------------------------------------

        let loader
        switch (modelType) {
          case 'PLY': loader = new PLYLoader(); break
          case 'OBJ': loader = new OBJLoader(); break
          case 'STL': loader = new STLLoader(); break
          case 'GLB': loader = new GLTFLoader(); break
          default: return
        }

        if (modelType === 'GLB') {
          loader.parse(arrayBuffer.buffer, '', (gltf) => processObject(gltf.scene))
        } else {
          const result = modelType === 'OBJ'
            ? loader.parse(new TextDecoder().decode(arrayBuffer))
            : loader.parse(arrayBuffer.buffer)

          let finalObject

          if (result.isBufferGeometry) {
            if (!result.attributes.normal) {
              result.computeVertexNormals()
            }

            const isPointCloud = modelType === 'PLY' && result.index === null

            if (isPointCloud) {
              const hasColors = result.hasAttribute('color')
              finalObject = new Points(
                result,
                new PointsMaterial({
                  size: 4,
                  sizeAttenuation: false,
                  vertexColors: hasColors,
                  color: hasColors ? 0xffffff : 0x333333
                })
              )
            } else {
              finalObject = new Mesh(result)
            }
          } else {
            finalObject = result
          }

          processObject(finalObject)
        }

        function processObject (obj) {
          const container = new Group()
          container.add(obj)

          const initialBox = new Box3().setFromObject(container)
          const center = new Vector3()
          initialBox.getCenter(center)
          obj.position.sub(center)

          const finalBox = new Box3().setFromObject(container)
          const size = new Vector3()
          finalBox.getSize(size)

          let maxDimension = Math.max(size.x, size.y, size.z)

          if (maxDimension === 0 || !isFinite(maxDimension)) {
            maxDimension = 1
          }

          obj.traverse((child) => {
            if (child.isMesh) {
              child.castShadow = true
              child.receiveShadow = true

              const isSTL = modelType === 'STL'
              const hasColors = child.geometry.hasAttribute('color') && !isSTL

              if (child.material.type === 'MeshBasicMaterial') {
                child.material = new MeshStandardMaterial({
                  color: hasColors ? 0xffffff : 0xdddddd,
                  roughness: 0.4,
                  metalness: 0.1,
                  vertexColors: hasColors,
                  side: DoubleSide,
                  polygonOffset: true,
                  polygonOffsetFactor: 1,
                  polygonOffsetUnits: 1
                })
              }
            }
          })

          setModelGroup(container)

          // Pass radius, minY, and exact dimensions to parent
          setModelSize({
            radius: maxDimension / 2,
            minY: finalBox.min.y,
            dimensions: { x: size.x, y: size.y, z: size.z }
          })

          setIsPreviewLoading(false)
        }
      } catch (err) {
        console.error('Loading error:', err)
        setIsPreviewLoading(false)
        setModelError(true)
      }
    }

    loadModel()
  }, [url, modelType, setIsPreviewLoading, setModelSize, setModelError, onProgress])

  // 2. Handle Solid Visibility Toggle
  useEffect(() => {
    if (!modelGroup) return
    modelGroup.traverse((child) => {
      if (child.isMesh && !child.userData.isEdge) {
        child.material.visible = solid
      }
    })
  }, [modelGroup, solid])

  // 3. Handle Edges Lifecycle
  useEffect(() => {
    if (!modelGroup || !edges) return

    const addedLines = []

    modelGroup.traverse((child) => {
      if (child.isMesh && !child.userData.isEdge) {
        const edgeGeom = new EdgesGeometry(child.geometry, edgesMinAngle)
        const material = new LineBasicMaterial({ color: edgesColor })
        const line = new LineSegments(edgeGeom, material)

        line.userData.isEdge = true
        child.add(line)
        addedLines.push({ parent: child, line })
      }
    })

    return () => {
      addedLines.forEach(({ parent, line }) => {
        parent.remove(line)
        line.geometry.dispose()
        line.material.dispose()
      })
    }
  }, [modelGroup, edges, edgesMinAngle, edgesColor])

  if (!modelGroup) return null

  return <primitive object={modelGroup} />
}

export { ModelLoader, CompatibleModelsExtensions }
